// Cuts short the wait for ICE candidate gathering.
//
// JsSIP sends the INVITE (or the answer) only when the browser reports that
// gathering is complete. The browser reports that after every network
// interface was tried against the STUN server, so one interface that cannot
// reach it (a VPN or an IPv6 adapter, for example) delays every call until its
// queries time out, which takes several seconds and sometimes much longer.
//
// For each candidate JsSIP hands the application a ready() function meaning
// "this is enough, send now". We call it:
//   - as soon as a server-reflexive (public) or relay candidate arrives, since
//     together with the host candidates that is what the far end needs; or
//   - a fixed time after the first candidate, when no such candidate shows up
//     (STUN blocked), so the call goes out with what we have.

export const ICE_GATHERING_TIMEOUT_MS = 2000;

const candidateType = (candidate) => {
  if (!candidate) return "";
  if (candidate.type) return candidate.type;
  const match = / typ (\w+)/.exec(candidate.candidate || "");
  return match ? match[1] : "";
};

/**
 * Returns { onCandidate, cancel } for one call session.
 * onCandidate takes the data of JsSIP's "icecandidate" event:
 * { candidate, ready }.
 */
export const createIceGatheringGuard = (timeoutMs = ICE_GATHERING_TIMEOUT_MS, log = () => {}) => {
  let timer = null;
  // ready() of the negotiation in progress; a re-INVITE brings a new one.
  let currentReady = null;

  const clear = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const finish = (reason) => {
    const ready = currentReady;
    clear();
    currentReady = null;
    if (ready) {
      log(reason);
      ready();
    }
  };

  const onCandidate = (data) => {
    if (!data || typeof data.ready !== "function") return;

    if (data.ready !== currentReady) {
      // First candidate of a new negotiation.
      clear();
      currentReady = data.ready;
    }

    const type = candidateType(data.candidate);
    if (type === "srflx" || type === "relay") {
      finish(`ICE: ${type} candidate received, sending without waiting for the rest`);
      return;
    }

    if (!timer) {
      timer = setTimeout(
        () => finish(`ICE: no public candidate after ${timeoutMs} ms, sending with the candidates gathered so far`),
        timeoutMs
      );
    }
  };

  const cancel = () => {
    clear();
    currentReady = null;
  };

  return { onCandidate, cancel };
};
